import { NotificationPermission } from '@/features/notifications/components/notification-permission.tsx';
import { NotificationList } from '@/features/notifications/components/notification-list.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/utils.ts';
import { Bell2Icon } from '@/assets/bell-2-icon.tsx';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { useGetNotificationsQuery } from '@/features/notifications/hooks/queries/get-all-notifications.ts';
import { useNotificationsStore } from '@/services/store/use-notifications-store.ts';
import { useShallow } from 'zustand/react/shallow';

export const NotificationBody = () => {
  const notificationFilter = useNotificationsStore((state) => state.notificationFilter);
  const { ref: scrollRef, inView } = useInView();

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useGetNotificationsQuery({
    notificationFilter,
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage().then();
    }
  }, [inView, fetchNextPage]);

  const hasNotifications = isLoading || data?.pages?.length;

  return (
    <div
      className={cn('max-h-[480px]', isLoading || (hasNotifications && 'overflow-y-scroll'))}
    >
      <div className="flex flex-col">
        <NotificationPermission className="sticky top-0 z-30 bg-white px-3 pt-2" />
        <NotificationFilters />

        {hasNotifications ? <NotificationList /> : <EmptyNotifications />}
      </div>

      <div ref={scrollRef} className="h-1">
        {isFetchingNextPage && (
          <div className="flex w-full items-center justify-center py-2 pb-6">
            <LoadingSpinner className="text-muted-foreground size-10" />
          </div>
        )}
      </div>
    </div>
  );
};

function NotificationFilters() {
  const [notificationFilter, setNotificationFilter] = useNotificationsStore(
    useShallow((state) => [state.notificationFilter, state.setNotificationFilter])
  );

  const { prefetchNotifications } = useGetNotificationsQuery({
    notificationFilter,
  });

  const filters = ['all', 'unread'] as const;

  return (
    <div className="sticky top-0 z-20 flex gap-2 bg-white px-4 py-2">
      {filters.map((item) => {
        const isSelected = item === notificationFilter;

        return (
          <button
            type="button"
            key={item}
            onMouseEnter={() => {
              if (item === 'unread') {
                prefetchNotifications('unread');
              }
            }}
            onClick={(e) => {
              e.stopPropagation();

              setNotificationFilter(item);
            }}
          >
            <div
              className={cn(
                'cursor-pointer rounded-full border border-transparent px-3 py-1 capitalize',
                isSelected ? 'bg-primary/5' : ''
              )}
            >
              {item}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function EmptyNotifications() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center justify-center space-y-4 pt-4 pb-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
          <Bell2Icon height="32" width="32" strokeWidth="1.5" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('notifications.noNotificationsTitle')}
          </h3>
          <p className="max-w-[280px] text-sm text-gray-500 dark:text-gray-400">
            {t('notifications.description')}
          </p>
        </div>
      </div>
    </div>
  );
}
